import { Controller } from '@hotwired/stimulus';

/**
 * Toggle visibility of a content section based on checkbox state.
 * Unchecks nested checkboxes when the parent is unchecked.
 * Optionally updates helper text (used in admin pilot period).
 *
 * Usage:
 *   <div data-controller="pilot-toggle">
 *     <input type="checkbox" data-pilot-toggle-target="checkbox"
 *            data-action="change->pilot-toggle#toggle"
 *            aria-controls="child-content-id" aria-expanded="false">
 *     <div data-pilot-toggle-target="content" id="child-content-id" class="hidden">...</div>
 *     <p data-pilot-toggle-target="helperText"></p>
 *   </div>
 */
export default class extends Controller {
  static targets = ['checkbox', 'content', 'helperText'];

  connect() {
    this.checkboxTarget.setAttribute('aria-expanded', String(this.checkboxTarget.checked));
  }

  toggle() {
    const isChecked = this.checkboxTarget.checked;
    this.checkboxTarget.setAttribute('aria-expanded', String(isChecked));

    if (isChecked) {
      this.reveal();
    } else {
      this.collapse();
    }
  }

  reveal() {
    const el = this.contentTarget;
    el.classList.remove('hidden');
    el.style.overflow = 'hidden';
    el.style.height = '0px';
    requestAnimationFrame(() => {
      el.style.transition = 'height 200ms ease-out, opacity 200ms ease-out';
      el.style.opacity = '0';
      requestAnimationFrame(() => {
        el.style.height = `${el.scrollHeight}px`;
        el.style.opacity = '1';
        const onEnd = () => {
          el.style.removeProperty('height');
          el.style.removeProperty('overflow');
          el.style.removeProperty('transition');
          el.style.removeProperty('opacity');
          el.removeEventListener('transitionend', onEnd);
        };
        el.addEventListener('transitionend', onEnd, { once: true });
      });
    });
  }

  collapse() {
    const el = this.contentTarget;
    el.style.height = `${el.scrollHeight}px`;
    el.style.overflow = 'hidden';
    requestAnimationFrame(() => {
      el.style.transition = 'height 200ms ease-in, opacity 150ms ease-in';
      el.style.height = '0px';
      el.style.opacity = '0';
      const onEnd = () => {
        el.classList.add('hidden');
        el.style.removeProperty('height');
        el.style.removeProperty('overflow');
        el.style.removeProperty('transition');
        el.style.removeProperty('opacity');
        el.removeEventListener('transitionend', onEnd);
        this.contentTarget
          .querySelectorAll('input[type="checkbox"]:checked')
          .forEach((cb) => (cb.checked = false));
        this.updateHelperText('');
      };
      el.addEventListener('transitionend', onEnd, { once: true });
    });
  }

  onDateChange(event) {
    const value = event.target.value;
    if (!value) {
      this.updateHelperText('');
      return;
    }

    const date = new Date(value + 'T00:00:00');
    const formatted = date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
    this.updateHelperText(`L'organisation aura accès jusqu'au ${formatted}.`);
  }

  updateHelperText(text) {
    if (this.hasHelperTextTarget) {
      this.helperTextTarget.textContent = text;
    }
  }
}
